import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../../environment";

// =============================
// Base URLs
// =============================
const COMPANY_BASE_URL = `${API_BASE_PATH}/clients/`;
const CLIENT_BASE_URL = COMPANY_BASE_URL;
const CLIENTS_BASE_URL = COMPANY_BASE_URL;
const MODULES_BASE_URL = `${API_BASE_PATH}/productmodules/`;
const PRODUCTS_BASE_URL = `${API_BASE_PATH}/products/`;

// ====== Error Extractor ======
const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, ""); // strip numeric codes
  }

  return detail || details || message;
};

// =============================
// Interfaces / Types
// =============================
export interface Client {
  company_license: string;
  company_name: string;
  email: string | null;
  server_name: string;
  company_batch: string;
  contact_number: number;
  product_id: number;
  modules: string[];
  status: string;
  client_id: string;
  product: {
    product_id: number;
    product_name: string;
    description?: string;
    created_at?: string;
    updated_at?: string;
  };
}

export interface Module {
  model_id: number;
  name: string;
}

export interface ProductInfo {
  product_id: number;
  product_name: string;
  description: string;
}

export interface FetchedClient {
  company_license: string;
  company_name: string;
  email: string;
  server_name: string;
  company_batch: string;
  contact_number: number;
  product_id: number;
  status: string;
  client_id: string;
  modules: Module[];
  product: ProductInfo;
}

export interface Company {
  company_license: string;
  company_name: string;
  email: string;
  server_name: string;
  company_batch: string;
  contact_number: number;
  product_id: number;
  modules: string[];
  status: string;
  client_id: string;
  product: {
    product_id: number;
    product_name: string;
    description?: string;
  };
}

export interface ModuleType {
  module_id: number;
  module_name: string;
  description: string;
}

export interface ProductType {
  product_id: number;
  product_name: string;
  description: string;
  created_at: string;
  updated_at: string;
  modules: ModuleType[];
}

// =============================
// CRUD Functions
// =============================

// ✅ Create a new client
export const createClient = async (
  clientData: Client,
  _token: string | null
): Promise<Client | null> => {
  try {
    const formData = new URLSearchParams();

    formData.append("company_license", clientData.company_license);
    formData.append("company_name", clientData.company_name);
    if (clientData.email) formData.append("email", clientData.email);
    formData.append("server_name", clientData.server_name);
    formData.append("company_batch", clientData.company_batch);
    formData.append("contact_number", clientData.contact_number.toString());
    formData.append("product_id", clientData.product_id.toString());
    formData.append("status", clientData.status);
    formData.append("client_id", clientData.client_id);

    if (clientData.modules && clientData.modules.length > 0) {
      formData.append("modules", clientData.modules.join(","));
    }

    const response = await axios.post(COMPANY_BASE_URL, formData, {
      headers: {
        accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded",
        authorization: "Bearer " + _token,
      },
    });
    toast.success("Company Added successfully");
    return response.data;
  } catch (error) {
    toast.error(extractErrorMessage(error));
    throw error;
  }
};

// ✅ Update an existing client
export const updateClient = async (
  clientId: string,
  clientData: Client,
  _token: string | null
): Promise<Client | null> => {
  try {
    const formData = new URLSearchParams();

    formData.append("company_license", clientData.company_license);
    formData.append("company_name", clientData.company_name);
    if (clientData.email) formData.append("email", clientData.email);
    formData.append("server_name", clientData.server_name);
    formData.append("company_batch", clientData.company_batch);
    formData.append("contact_number", clientData.contact_number.toString());
    formData.append("product_id", clientData.product_id.toString());
    formData.append("status", clientData.status);

    clientData.modules.forEach((mod) => formData.append("modules", mod));

    formData.append(
      "product.product_id",
      clientData.product.product_id.toString()
    );
    formData.append("product.product_name", clientData.product.product_name);
    if (clientData.product.description) {
      formData.append("product.description", clientData.product.description);
    }

    const response = await axios.put(
      `${COMPANY_BASE_URL}/${clientId}`,
      formData,
      {
        headers: {
          accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
          authorization: "Bearer " + _token,
        },
      }
    );
    toast.success("Company updated successfully");
    return response.data;
  } catch (error) {
    toast.error(extractErrorMessage(error));
    throw error;
  }
};

// ✅ Delete a client
export const deleteClient = async (
  clientId: string,
  _token: string | null
): Promise<void> => {
  try {
    await axios.delete(`${COMPANY_BASE_URL}client/delete/${clientId}`, {
      headers: {
        accept: "application/json",
        authorization: "Bearer " + _token,
      },
    });
    toast.success("Company Deleted successfully");
  } catch (error: any) {
    toast.error(extractErrorMessage(error));
    throw error;
  }
};

// =============================
// Fetch Functions
// =============================
export const fetchClients = async (
  _token: string | null
): Promise<FetchedClient[]> => {
  try {
    const [clientsRes, modulesRes] = await Promise.all([
      axios.get(CLIENTS_BASE_URL, {
        headers: {
          accept: "application/json",
          authorization: "Bearer " + _token,
        },
      }),
      axios.get(MODULES_BASE_URL, {
        headers: {
          accept: "application/json",
          authorization: "Bearer " + _token,
        },
      }),
    ]);

    const clientsData = clientsRes.data;
    const modulesData = modulesRes.data;

    const moduleMap = new Map<number, string>();
    modulesData.forEach((module: any) => {
      moduleMap.set(module.module_id, module.module_name.trim());
    });

    return clientsData.map((client: any) => {
      const moduleList: Module[] =
        client.modules && client.modules.length > 0
          ? client.modules.flatMap((mod: string | number) =>
              typeof mod === "string"
                ? mod.split(",").map((id) => ({
                    model_id: Number(id.trim()),
                    name: moduleMap.get(Number(id.trim())) || `Module ${id}`,
                  }))
                : [
                    {
                      model_id: Number(mod),
                      name: moduleMap.get(Number(mod)) || `Module ${mod}`,
                    },
                  ]
            )
          : [];

      return {
        ...client,
        modules: moduleList,
        product: client.product
          ? {
              product_id: client.product.product_id,
              product_name: client.product.product_name,
              description: client.product.description,
            }
          : {
              product_id: 0,
              product_name: "Unknown Product",
              description: "",
            },
      };
    });
  } catch (error) {
    toast.error(extractErrorMessage(error));
    throw error;
  }
};

export const getCompany = async (_token: string | null): Promise<Company[]> => {
  try {
    const response = await axios.get(`${COMPANY_BASE_URL}`, {
      headers: {
        accept: "application/json",
        authorization: "Bearer " + _token,
      },
    });
    return response.data;
  } catch (error) {
    toast.error(extractErrorMessage(error));
    throw error;
  }
};

export const getClientWidgets = async (_token: string | null) => {
  try {
    const response = await axios.get(`${COMPANY_BASE_URL}widgets/`, {
      headers: {
        accept: "application/json",
        authorization: "Bearer " + _token,
      },
    });
    return response.data;
  } catch (error) {
    toast.error(extractErrorMessage(error));
    throw error;
  }
};

export const fetchProductList = async (
  _token: string | null
): Promise<ProductType[]> => {
  try {
    const response = await axios.get<ProductType[]>(PRODUCTS_BASE_URL, {
      headers: {
        Accept: "application/json",
        authorization: "Bearer " + _token,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching product list:", error);
    toast.error(extractErrorMessage(error));
    throw error;
  }
};

// =============================
// Export / Import Functions (Token Added)
// =============================

// Export Selected Clients as PDF
export const exportSelectedClientsPdf = async (
  clientIds: string[],
  _token: string | null
) => {
  try {
    if (clientIds.length === 0) {
      toast.warning("No client IDs selected to export as PDF.");
      return;
    }

    for (const id of clientIds) {
      const response = await axios.get(
        `${CLIENT_BASE_URL}export/pdf/${id}`,
        {
          headers: {
            accept: "application/json",
            authorization: "Bearer " + _token,
          },
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Client_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    }

    toast.success("Selected client PDFs downloaded.");
  } catch (error) {
    console.error("Export PDF failed:", error);
    toast.error(extractErrorMessage(error));
  }
};

// Export Selected Clients as Excel
export const exportSelectedClientsExcel = async (
  clientIds: string[],
  _token: string | null
) => {
  try {
    if (clientIds.length === 0) {
      toast.warning("No client IDs selected to export as Excel.");
      return;
    }

    for (const id of clientIds) {
      const response = await axios.get(
        `${CLIENT_BASE_URL}export/excel/${id}`,
        {
          headers: {
            accept: "application/json",
            authorization: "Bearer " + _token,
          },
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Client_${id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    }

    toast.success("Selected client Excels downloaded.");
  } catch (error) {
    console.error("Export Excel failed:", error);
    toast.error(extractErrorMessage(error));
  }
};

// Export All Clients as PDF
export const exportAllClientsPdf = async (_token: string | null) => {
  try {
    const response = await axios.get(
      `${CLIENT_BASE_URL}clients/export/pdf`,
      {
        headers: {
          accept: "application/json",
          authorization: "Bearer " + _token,
        },
        responseType: "blob",
      }
    );

    const blob = new Blob([response.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "All_Clients.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    toast.success("All clients PDF downloaded.");
  } catch (error) {
    console.error("Failed to export all clients as PDF:", error);
    toast.error(extractErrorMessage(error));
  }
};

// Export All Clients as Excel
export const exportAllClientsExcel = async (_token: string | null) => {
  try {
    const response = await axios.get(
      `${CLIENT_BASE_URL}clients/export/excel`,
      {
        headers: {
          accept: "application/json",
          authorization: "Bearer " + _token,
        },
        responseType: "blob",
      }
    );

    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "All_Clients.xlsx";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    toast.success("All clients Excel downloaded.");
  } catch (error) {
    console.error("Failed to export all clients as Excel:", error);
    toast.error(extractErrorMessage(error));
  }
};

// Update existing clients from Excel
export const updateClientsFromExcel = async (
  file: File,
  _token: string | null
): Promise<void> => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    await axios.put(`${CLIENT_BASE_URL}excel-update`, formData, {
      headers: {
        accept: "application/json",
        authorization: "Bearer " + _token,
        "Content-Type": "multipart/form-data",
      },
    });

    toast.success("Client records updated successfully from Excel.");
  } catch (error) {
    console.error("Excel update failed:", error);
    toast.error(extractErrorMessage(error));
    throw error;
  }
};

// Import new clients from Excel
export const importClientsFromExcel = async (
  file: File,
  _token: string | null
): Promise<void> => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    await axios.post(`${CLIENT_BASE_URL}import-excel`, formData, {
      headers: {
        accept: "application/json",
        authorization: "Bearer " + _token,
        "Content-Type": "multipart/form-data",
      },
    });

    toast.success("Clients imported successfully from Excel.");
  } catch (error) {
    console.error("Excel import failed:", error);
    toast.error(extractErrorMessage(error));
    throw error;
  }
};
